<template>
    <div class="gift-list-picker">
        <SearchableDropdown
            v-model="selectedArticle"
            :list="[]"
            :option-key="(article: Article) => article.articleId"
            :option-label="(article: Article) => `#${article.articleId} - ${article.title || 'Untitled'}`"
            placeholder="Search for a gift list..."
            :options="{
                searchable: true,
                disabled: disabled,
                lazyLoad: true,
                loadFunction: loadArticles,
                pageSize: 20
            }"
            @update:model-value="onArticleSelected"
        >
            <template #option="{ option }">
                <div class="gift-list-picker-option">
                    <span class="gift-list-picker-option-id">#{{ option.articleId }}</span>
                    <span class="gift-list-picker-option-title">{{ option.title || 'Untitled' }}</span>
                </div>
            </template>
        </SearchableDropdown>
        <div v-show="isSelectingArticle" class="gift-list-picker-loading">
            <span class="icon"><img :src="`/Assets/img/spin.svg`" /></span> Loading gift list...
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch } from 'vue'
import api from '@/api'
import { Article } from '@/classes/Articles'
import SearchableDropdown from './Backend/SearchableDropdown.vue'

export default defineComponent({
    name: 'GiftListPicker',
    components: {
        SearchableDropdown
    },
    props: {
        modelValue: {
            type: Number,
            default: null
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    emits: ['update:modelValue', 'articleSelected'],
    setup(props, { emit }) {
        const selectedArticle = ref<Article | null>(null)
        const isSelectingArticle = ref(false)

        const loadArticles = async (page: number, searchText: string): Promise<Article[]> => {
            try {
                const response = await api.getArticles(20, page, 0, true, searchText)
                return response.data.map((article: any) => new Article(article))
            } catch (error) {
                console.error('Failed to load gift lists:', error)
                return []
            }
        }

        const onArticleSelected = async (article: Article | null) => {
            if (!article || props.disabled) return
            
            isSelectingArticle.value = true
            emit('update:modelValue', article.articleId)
            
            try {
                const response = await api.getArticleDetails(article.articleId, false)
                if (response && response.data) {
                    const fullArticle = new Article(response.data)
                    emit('articleSelected', fullArticle)
                } else {
                    console.error('No article data returned from API for articleId:', article.articleId)
                }
            } catch (error) {
                console.error('Failed to fetch article details:', error)
            } finally {
                isSelectingArticle.value = false
            }
        }

        // Watch for external modelValue changes
        watch(() => props.modelValue, async (newValue) => {
            if (newValue && (!selectedArticle.value || selectedArticle.value.articleId !== newValue)) {
                try {
                    const response = await api.getArticleDetails(newValue, false)
                    if (response && response.data) {
                        selectedArticle.value = new Article(response.data)
                    }
                } catch (error) {
                    console.error('Failed to load article:', error)
                }
            } else if (!newValue) {
                selectedArticle.value = null
            }
        }, { immediate: true })

        return {
            selectedArticle,
            isSelectingArticle,
            loadArticles,
            onArticleSelected
        }
    }
})
</script>

<style scoped>
.gift-list-picker {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.gift-list-picker-option {
    display: flex;
    align-items: center;
    gap: 8px;
}

.gift-list-picker-option-id {
    font-weight: 600;
    color: #2196F3;
    min-width: 50px;
}

.gift-list-picker-option-title {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.gift-list-picker-loading {
    text-align: center;
    padding: 10px;
    color: #666;
    font-size: 13px;
}

.gift-list-picker-loading .icon {
    display: inline-block;
    margin-right: 8px;
}

.gift-list-picker-loading .icon img {
    width: 16px;
    height: 16px;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}
</style>

